import React from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  type PaperProps,
  type SxProps,
  type Theme,
  type TooltipProps,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import { Menu, MenuItem } from '@mui/material'
import DashboardCard from './DashboardCard'
import PrepDashboardPlaceholderItem from './PrepDashboardPlaceholderItem'
import { type DashboardCardItemActions, type DashboardCardSettingsContext } from './types'
import {
  PREP_FETCH_RETRY_DELAYS_MS,
  buildCategoryChipSx,
  createItemTooltipSlotProps,
  fetchPrepItemDetailWithRetry,
  extractRequestId,
  isRetriablePrepFetchError,
  normalizePrepDashboardItems,
  type PrepDashboardNormalizedEntry,
  wait,
  computeRetryDelay,
  withRequestId,
} from './utils'
import { getCategoryColor, getChipColors } from '../../../utils/colorByCategory'
import { shortenCategoryLabel } from '../../../utils/categoryLabel'
import {
  addPrepItemToMyPrep,
  dismissForAll as dismissForAllApi,
  dismissPrepItem,
  getCategoryItems,
  removePrepItemFromMyPrep,
  undoDismissPrepItem,
  type PrepDashboardCard,
  type PrepListItem,
} from '../../../api/prep'
import usePrepListStore from '../../../store/prepListStore'
import usePrepUsageStore from '../../../store/prepUsageStore'
import useStationStore from '../../../store/station'
import useUserSettingsStore from '../../../store/userSettings'
import useShowClockPrepUsageStore from '../../../store/showClockPrepUsageStore'
import usePrepDashboardStore from '../../../store/prepDashboardStore'
import { syncAuthoritativeMyPrepOrder } from '../../../store/prepMembershipSync'
import {
  beginPrepUsedOptimistic,
  commitPrepUsed,
  rollbackPrepUsed,
} from '../../../store/prepUsedSync'
import { useMe } from '../../../app/MeContext'
import { formatRelativeTime } from '../../../utils/relativeTime'
import { resolveEpochMs, formatStationTime, resolveStationTimeZone, stationDayDiff } from '../../../utils/time'
import { headlineClampSx } from '../styles'
import { coerceDetailToPrepListItem } from '../../../utils/prepItemNormalization'
import EditPrepCategoriesModal from '../EditPrepCategoriesModal'
import AddPrepToShowClockModal from '../AddPrepToShowClockModal'
import {
  capShowClockUsage,
  capShowClockUsageItems,
  formatShowClockUsageItemLabel,
  formatShowClockUsageLabel,
} from '../../../utils/showClockUsageLabels'

const REFRESH_INTERVAL_MS = 10 * 60 * 1000
export type PrepDashboardCategoryCard = PrepDashboardCard & {
  type: 'category'
  categoryId: number
  categorySlug?: string
  categoryName?: string
}

type SnackbarAction = 'undo-dismiss'

type SnackbarState = {
  message: string
  severity: 'success' | 'error' | 'info'
  action?: SnackbarAction
}

type DismissSnapshot = {
  item: PrepListItem
  index: number
  entryIndex: number
  wasInMyPrep: boolean
}

interface CategoryCardProps {
  card: PrepDashboardCategoryCard
  onChange: (patch: Partial<PrepDashboardCategoryCard>) => void
  onSelectItem?: (item: PrepListItem) => void
  activeItemId?: number
  onActiveItemMissing?: () => void
  isSuperadmin?: boolean
  onRegisterActions?: (actions: DashboardCardItemActions | null) => void
  onItemsChange?: (items: PrepListItem[], total: number) => void
  onOpenSettings?: (context: DashboardCardSettingsContext) => void
  initialDelayMs?: number
  onRefreshCategories?: (categoryIds: number[]) => void
  snapshotData?: {
    items: PrepListItem[]
    total: number
    loading: boolean
    refreshing: boolean
    error: string | null
    version: number
  }
  onRequestSnapshotRefresh?: () => Promise<void>
  onRequestMyPrepRefresh?: (expected?: { itemId: number; inMyPrep: boolean }) => Promise<void>
}

const sanitizePositiveInt = (value: number, fallback: number, min: number, max: number): number => {
  if (!Number.isFinite(value)) {
    return fallback
  }
  const normalized = Math.floor(value)
  return Math.min(max, Math.max(min, normalized))
}

const collectCategoryIds = (item: PrepListItem): number[] => {
  const ids = new Set<number>()
  item.categories?.forEach((category) => {
    if (typeof category?.id === 'number' && category.id > 0) {
      ids.add(category.id)
    }
  })
  if (typeof item.primary_category_id === 'number' && item.primary_category_id > 0) {
    ids.add(item.primary_category_id)
  }
  return Array.from(ids)
}

const CategoryCard: React.FC<CategoryCardProps> = ({
  card,
  onChange,
  onSelectItem,
  activeItemId,
  onActiveItemMissing,
  isSuperadmin = false,
  onRegisterActions,
  onItemsChange,
  onOpenSettings,
  initialDelayMs = 0,
  onRefreshCategories,
  snapshotData,
  onRequestSnapshotRefresh,
  onRequestMyPrepRefresh,
}) => {
  const me = useMe()
  const usesSnapshot = snapshotData !== undefined
  const debugLog = React.useCallback(
    (...args: unknown[]) => {
      if (typeof window === 'undefined') {
        return
      }
      const bootstrapDebug = Boolean((window as typeof window & { __RPA_BOOTSTRAP__?: { debug?: boolean } }).__RPA_BOOTSTRAP__?.debug)
      const debug =
        window.__RPA_DEBUG?.prepDashboard === true ||
        window.__RPA_DEBUG?.prep === true ||
        window.__RPA_DEBUG?.net === true ||
        window.__RPA_DEBUG?.prep?.dashboard === true ||
        bootstrapDebug
      if (!debug) {
        return
      }
      // eslint-disable-next-line no-console
      console.debug('[prep][dashboard]', ...args)
    },
    [],
  )
  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone ?? null)
  const updateMyItemInStore = usePrepListStore((state) => state.updateMyItem)
  const incMyCount = usePrepListStore((state) => state.incMyCount)
  const decMyCount = usePrepListStore((state) => state.decMyCount)
  const incMyCategoryCounts = usePrepListStore((state) => state.incMyCategoryCounts)
  const decMyCategoryCounts = usePrepListStore((state) => state.decMyCategoryCounts)
  const myItems = usePrepListStore((state) => state.myItems)
  const myIdsSet = usePrepListStore((state) => state.myIds)
  const categoriesById = usePrepListStore((state) => state.categoriesById)
  const usageByPrepId = usePrepUsageStore((state) => state.usageByPrepId)
  const fetchUsageFlags = usePrepUsageStore((state) => state.fetchUsageFlags)
  const markUsed = usePrepUsageStore((state) => state.markUsed)
  const globalHeadlineLines = useUserSettingsStore((state) => state.prep.headlineLines ?? 2)
  const ageMaxDays = useUserSettingsStore((state) => state.prep.ageMaxDays)
  const dateBasis = useUserSettingsStore((state) => state.prep.dateBasis)
  const globalListShowImages = useUserSettingsStore((state) => state.prep.listShowImages)
  const headlineLines = card.headlineLines ?? globalHeadlineLines
  const listShowImages = card.listShowImages ?? globalListShowImages

  const ensureShowClockUsage = useShowClockPrepUsageStore((state) => state.ensureUsage)
  const clocksByPrepId = useShowClockPrepUsageStore((state) => state.clocksByPrepId)
  const [addToShowClockTarget, setAddToShowClockTarget] = React.useState<PrepListItem | null>(null)

  const titleFromStore = React.useMemo(() => {
    return card.categoryName ?? categoriesById?.[card.categoryId]?.name ?? null
  }, [card.categoryName, card.categoryId, categoriesById])

  const [entries, setEntries] = React.useState<PrepDashboardNormalizedEntry[]>([])
  const [total, setTotal] = React.useState<number>(0)
  const [totalKnown, setTotalKnown] = React.useState(false)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [refreshing, setRefreshing] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [retryingAttempt, setRetryingAttempt] = React.useState<number | null>(null)
  const [manualRetryVisible, setManualRetryVisible] = React.useState(false)
  const [busyIds, setBusyIds] = React.useState<number[]>([])
  const [menuAnchor, setMenuAnchor] = React.useState<Record<number, HTMLElement | null>>({})
  const [placeholderRefreshingIds, setPlaceholderRefreshingIds] = React.useState<number[]>([])
  const [editCategoriesItem, setEditCategoriesItem] = React.useState<PrepListItem | null>(null)

  const itemTooltipSlotProps = React.useMemo(() => createItemTooltipSlotProps(), [])
  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)
  const [categoryName, setCategoryName] = React.useState<string>(
    typeof card.categoryName === 'string' ? card.categoryName.trim() : '',
  )
  const [categorySlug, setCategorySlug] = React.useState<string | null>(
    typeof card.categorySlug === 'string'
      ? card.categorySlug.trim() || null
      : null,
  )

  React.useEffect(() => {
    if (usesSnapshot || !stationId || stationId <= 0 || entries.length === 0) {
      return
    }
    const ids = entries
      .map((entry) => entry.item?.id ?? null)
      .filter((id): id is number => typeof id === 'number' && id > 0)
    if (ids.length === 0) {
      return
    }
    void ensureShowClockUsage(stationId, ids)
  }, [ensureShowClockUsage, entries, stationId, usesSnapshot])

  const cardRef = React.useRef(card)
  const itemsRef = React.useRef<PrepListItem[]>([])
  const entriesRef = React.useRef<PrepDashboardNormalizedEntry[]>([])
  const totalRef = React.useRef(0)
  const totalKnownRef = React.useRef(false)
  const refreshTimerRef = React.useRef<number | null>(null)
  const activeRequestRef = React.useRef<AbortController | null>(null)
  const fetchSequenceRef = React.useRef(0)
  const initialFetchDoneRef = React.useRef(false)
  const initialDelayRef = React.useRef(initialDelayMs)
  const lastDismissedRef = React.useRef<DismissSnapshot | null>(null)
  const categoryNameRef = React.useRef('')
  const categorySlugRef = React.useRef<string | null>(null)
  const onItemsChangeRef = React.useRef(onItemsChange)
  const manualRetryTimerRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    cardRef.current = card
  }, [card])

  React.useEffect(() => {
    if (!usesSnapshot) {
      void usePrepListStore.getState().ensureFullMyIds().catch(() => undefined)
    }
  }, [usesSnapshot])

  React.useEffect(() => {
    onItemsChangeRef.current = onItemsChange
  }, [onItemsChange])

  React.useEffect(() => {
    if (!snapshotData) {
      return
    }

    const normalizedEntries = normalizePrepDashboardItems(snapshotData.items)
    const normalizedItems = normalizedEntries.flatMap((entry) =>
      entry.kind === 'item' ? [entry.item] : [],
    )
    entriesRef.current = normalizedEntries
    itemsRef.current = normalizedItems
    totalRef.current = Math.max(0, snapshotData.total)
    totalKnownRef.current = true
    setEntries(normalizedEntries)
    setTotal(totalRef.current)
    setTotalKnown(true)
    setLoading(snapshotData.loading)
    setRefreshing(snapshotData.refreshing)
    setError(snapshotData.error)
    setManualRetryVisible(Boolean(snapshotData.error))
    onItemsChangeRef.current?.(normalizedItems, totalRef.current)
  }, [
    snapshotData?.error,
    snapshotData?.items,
    snapshotData?.loading,
    snapshotData?.refreshing,
    snapshotData?.total,
    snapshotData?.version,
  ])

  React.useEffect(() => {
    categoryNameRef.current = categoryName
  }, [categoryName])

  React.useEffect(() => {
    categorySlugRef.current = categorySlug
  }, [categorySlug])

  React.useEffect(() => {
    if (refreshTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    if (manualRetryTimerRef.current && typeof window !== 'undefined') {
      window.clearTimeout(manualRetryTimerRef.current)
      manualRetryTimerRef.current = null
    }
    if (activeRequestRef.current) {
      activeRequestRef.current.abort()
      activeRequestRef.current = null
    }
    return () => {
      if (refreshTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      if (activeRequestRef.current) {
        activeRequestRef.current.abort()
        activeRequestRef.current = null
      }
      if (manualRetryTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(manualRetryTimerRef.current)
        manualRetryTimerRef.current = null
      }
    }
  }, [])

  React.useEffect(() => {
    initialFetchDoneRef.current = false
    initialDelayRef.current = initialDelayMs
  }, [card.id, card.categoryId])

  const fallbackCategoryDetails = React.useMemo(() => {
    if (!card.categoryId) {
      return { name: '', slug: null as string | null }
    }

    const cached = categoriesById?.[card.categoryId]
    const cachedName = typeof cached?.name === 'string' ? cached.name.trim() : ''
    const cachedSlug = typeof cached?.slug === 'string' ? cached.slug.trim() : ''
    if (cachedName || cachedSlug) {
      return { name: cachedName, slug: cachedSlug || null }
    }

    if (!Array.isArray(myItems)) {
      return { name: '', slug: null as string | null }
    }

    for (const item of myItems) {
      if (!item || !Array.isArray(item.categories)) {
        continue
      }

      const match = item.categories.find((category) => category?.id === card.categoryId)

      if (match) {
        const name = typeof match.name === 'string' ? match.name.trim() : ''
        const slugRaw = typeof match.slug === 'string' ? match.slug.trim() : ''
        if (name || slugRaw) {
          return { name, slug: slugRaw || null }
        }
      }
    }

    return { name: '', slug: null as string | null }
  }, [card.categoryId, categoriesById, myItems])

  const fallbackCategoryName = fallbackCategoryDetails.name
  const fallbackCategorySlug = fallbackCategoryDetails.slug

  React.useEffect(() => {
    if (!categoryNameRef.current && fallbackCategoryName) {
      setCategoryName(fallbackCategoryName)
    }
  }, [fallbackCategoryName])

  React.useEffect(() => {
    if (!categorySlugRef.current && fallbackCategorySlug) {
      setCategorySlug(fallbackCategorySlug)
    }
  }, [fallbackCategorySlug])

  React.useEffect(() => {
    setCategoryName('')
    setCategorySlug(null)
  }, [card.categoryId])

  React.useEffect(() => {
    const provided = typeof card.categoryName === 'string' ? card.categoryName.trim() : ''
    if (provided && provided !== categoryNameRef.current) {
      setCategoryName(provided)
    }
  }, [card.categoryName])

  React.useEffect(() => {
    const provided = typeof card.categorySlug === 'string' ? card.categorySlug.trim() : ''
    if (provided && provided !== categorySlugRef.current) {
      setCategorySlug(provided)
    }
  }, [card.categorySlug])

  const markBusy = React.useCallback((id: number) => {
    setBusyIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  const clearBusy = React.useCallback((id: number) => {
    setBusyIds((prev) => prev.filter((value) => value !== id))
  }, [])

  const rollbackRemoval = React.useCallback(
    ({
      previousItems,
      previousEntries,
      previousTotal,
    }: {
      previousItems: PrepListItem[]
      previousEntries: PrepDashboardNormalizedEntry[]
      previousTotal: number
    }) => {
      itemsRef.current = previousItems
      entriesRef.current = previousEntries
      setEntries(previousEntries)
      totalRef.current = previousTotal
      setTotal(previousTotal)
      onItemsChangeRef.current?.(previousItems, previousTotal)
    },
    [],
  )

  const optimisticRemove = React.useCallback(
    (item: PrepListItem): {
      previousItems: PrepListItem[]
      previousEntries: PrepDashboardNormalizedEntry[]
      previousTotal: number
      removed: boolean
      nextTotal: number
      index: number
      entryIndex: number
    } => {
      const previousItems = itemsRef.current
      const previousEntries = entriesRef.current
      const previousTotal = totalRef.current
      const index = previousItems.findIndex((entry) => entry.id === item.id)
      if (index === -1) {
        return {
          previousItems,
          previousEntries,
          previousTotal,
          removed: false,
          nextTotal: previousTotal,
          index,
          entryIndex: -1,
        }
      }

      const entryIndex = previousEntries.findIndex(
        (entry) => entry.kind === 'item' && entry.item.id === item.id,
      )
      const nextEntries =
        entryIndex === -1
          ? previousEntries
          : [
              ...previousEntries.slice(0, entryIndex),
              ...previousEntries.slice(entryIndex + 1),
            ]
      const nextItems = [...previousItems.slice(0, index), ...previousItems.slice(index + 1)]

      entriesRef.current = nextEntries
      itemsRef.current = nextItems
      setEntries(nextEntries)

      const nextTotal = Math.max(0, previousTotal - 1)
      totalRef.current = nextTotal
      setTotal(nextTotal)
      onItemsChangeRef.current?.(nextItems, nextTotal)

      return {
        previousItems,
        previousEntries,
        previousTotal,
        removed: true,
        nextTotal,
        index,
        entryIndex,
      }
    },
    [],
  )

  const updateItemLocal = React.useCallback(
    (itemId: number, patch: Partial<PrepListItem>): PrepListItem | null => {
      const currentItems = itemsRef.current
      let itemChanged = false
      const nextItems = currentItems.map((entry) => {
        if (entry.id !== itemId) {
          return entry
        }
        itemChanged = true
        return { ...entry, ...patch }
      })

      const currentEntries = entriesRef.current
      let entryChanged = false
      const nextEntries = currentEntries.map((entry) => {
        if (entry.kind !== 'item' || entry.item.id !== itemId) {
          return entry
        }
        entryChanged = true
        return { kind: 'item', item: { ...entry.item, ...patch } }
      })

      if (!itemChanged && !entryChanged) {
        return currentItems.find((entry) => entry.id === itemId) ?? null
      }

      itemsRef.current = nextItems
      entriesRef.current = nextEntries
      setEntries(nextEntries)
      onItemsChangeRef.current?.(nextItems, totalRef.current)
      return nextItems.find((entry) => entry.id === itemId) ?? null
    },
    [],
  )

  const adjustAfterRemoval = React.useCallback(
    (nextTotal: number) => {
      const current = cardRef.current
      const perPageValue = Math.max(1, current.itemsPerPage || 1)
      const maxPage = Math.max(1, Math.ceil(Math.max(0, nextTotal) / perPageValue))
      if (current.page > maxPage) {
        onChange({ page: maxPage })
        return
      }
      void fetchItems({ background: true })
    },
    [onChange],
  )

  const fetchItems = React.useCallback(
    async ({ background = false }: { background?: boolean } = {}) => {
      if (usesSnapshot) {
        await onRequestSnapshotRefresh?.()
        return
      }

      const snapshot = cardRef.current
      const categoryId = snapshot.categoryId
      if (!categoryId) {
        itemsRef.current = []
        entriesRef.current = []
        setEntries([])
        totalRef.current = 0
        totalKnownRef.current = false
        setTotalKnown(false)
        setTotal(0)
        setError(null)
        setLoading(false)
        setRefreshing(false)
        setRetryingAttempt(null)
        if (refreshTimerRef.current && typeof window !== 'undefined') {
          window.clearTimeout(refreshTimerRef.current)
          refreshTimerRef.current = null
        }
        return
      }

      const sequenceId = fetchSequenceRef.current + 1
      fetchSequenceRef.current = sequenceId

      if (refreshTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }

      if (manualRetryTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(manualRetryTimerRef.current)
        manualRetryTimerRef.current = null
      }

      if (!background && initialDelayMs > 0 && !initialFetchDoneRef.current) {
        debugLog('initial delay', { cardId: snapshot.id, categoryId, delayMs: initialDelayMs })
      }

      if (activeRequestRef.current) {
        activeRequestRef.current.abort()
        activeRequestRef.current = null
      }

      if (background) {
        setRefreshing(true)
      } else {
        setLoading(true)
        setError(null)
        setManualRetryVisible(false)
      }

      const attemptCount = PREP_FETCH_RETRY_DELAYS_MS.length + 1
      let finalError: unknown = null
      let succeeded = false
      let aborted = false

      for (let attemptIndex = 0; attemptIndex < attemptCount; attemptIndex += 1) {
        setRetryingAttempt(attemptIndex > 0 ? attemptIndex + 1 : null)

        if (fetchSequenceRef.current !== sequenceId) {
          aborted = true
          break
        }

        if (cardRef.current.collapsed) {
          break
        }

        const controller = new AbortController()
        activeRequestRef.current = controller
        const startedAt = Date.now()
        debugLog('fetch start', {
          cardId: snapshot.id,
          categoryId,
          attempt: attemptIndex + 1,
          page: snapshot.page,
          perPage: snapshot.itemsPerPage,
          background,
        })

        try {
          const response = await getCategoryItems(
            categoryId,
            { page: snapshot.page, perPage: snapshot.itemsPerPage, ageMaxDays, dateBasis },
            { signal: controller.signal },
          )

          if (controller.signal.aborted || fetchSequenceRef.current !== sequenceId) {
            aborted = true
            break
          }

          const normalizedEntries = normalizePrepDashboardItems(response.items)
          const normalizedItems = normalizedEntries.flatMap((entry) =>
            entry.kind === 'item' ? [entry.item] : [],
          )

          if (stationId > 0 && normalizedItems.length > 0) {
            void fetchUsageFlags(
              stationId,
              normalizedItems.map((entry) => entry.id),
            )
          }

          const derivedCategory =
            normalizedItems
              .map((entry) => {
                if (!Array.isArray(entry.categories)) {
                  return null
                }

                const match = entry.categories.find((category) => category?.id === categoryId)
                if (!match) {
                  return null
                }

                const name =
                  typeof match.name === 'string' && match.name.trim().length > 0
                    ? match.name.trim()
                    : null
                const slug =
                  typeof match.slug === 'string' && match.slug.trim().length > 0
                    ? match.slug.trim()
                    : null

                if (!name && !slug) {
                  return null
                }

                return { name, slug }
              })
              .find((value) => value !== null) ?? null

          if (derivedCategory?.name && derivedCategory.name !== categoryNameRef.current) {
            setCategoryName(derivedCategory.name)
          }

          if (derivedCategory?.slug && derivedCategory.slug !== categorySlugRef.current) {
            setCategorySlug(derivedCategory.slug)
          }

          itemsRef.current = normalizedItems
          entriesRef.current = normalizedEntries
          setEntries(normalizedEntries)
          const nextTotal = Math.max(
            0,
            Number.isFinite(response.total)
              ? Math.floor(Number(response.total))
              : normalizedEntries.length,
          )
          totalRef.current = nextTotal
          totalKnownRef.current = true
          setTotalKnown(true)
          setTotal(nextTotal)
          setError(null)
          setManualRetryVisible(false)
          onItemsChangeRef.current?.(normalizedItems, nextTotal)
          succeeded = true
          finalError = null
          const durationMs = Date.now() - startedAt
          debugLog('fetch success', {
            cardId: snapshot.id,
            categoryId,
            attempt: attemptIndex + 1,
            durationMs,
            total: nextTotal,
            items: normalizedItems.length,
          })
          break
        } catch (error) {
          if (controller.signal.aborted || fetchSequenceRef.current !== sequenceId) {
            aborted = true
            debugLog('fetch aborted', {
              cardId: snapshot.id,
              categoryId,
              attempt: attemptIndex + 1,
            })
            break
          }

          finalError = error

          const hasMoreAttempts = attemptIndex < attemptCount - 1
          if (!hasMoreAttempts || !isRetriablePrepFetchError(error, { allow508: true })) {
            break
          }

          const delayMs = computeRetryDelay(PREP_FETCH_RETRY_DELAYS_MS[attemptIndex] ?? 0)
          debugLog('fetch retry', {
            cardId: snapshot.id,
            categoryId,
            attempt: attemptIndex + 1,
            nextAttemptInMs: delayMs,
            status: (error as { status?: unknown })?.status,
          })
          if (fetchSequenceRef.current !== sequenceId) {
            aborted = true
            break
          }
          if (cardRef.current.collapsed) {
            break
          }
          if (delayMs > 0) {
            await wait(delayMs)
          }
        } finally {
          if (activeRequestRef.current === controller) {
            activeRequestRef.current = null
          }
        }
      }

      setRetryingAttempt(null)

      if (aborted) {
        if (fetchSequenceRef.current === sequenceId) {
          if (background) {
            setRefreshing(false)
          } else {
            setLoading(false)
          }
        }
        return
      }

      if (!succeeded && finalError) {
        const requestId = extractRequestId(finalError)
        const message =
          finalError && typeof finalError === 'object' && 'message' in finalError
            ? String((finalError as { message?: unknown }).message ?? 'Failed to load category items.')
            : 'Failed to load category items.'
        setError(withRequestId(message, requestId))
        debugLog('fetch failed', {
          cardId: snapshot.id,
          categoryId,
          requestId,
          error: finalError,
        })
        if (typeof window !== 'undefined') {
          manualRetryTimerRef.current = window.setTimeout(() => {
            setManualRetryVisible(true)
            debugLog('manual retry armed', { cardId: snapshot.id, categoryId })
          }, 20000)
        }
      }

      if (background) {
        setRefreshing(false)
      } else {
        setLoading(false)
      }

      if (typeof window !== 'undefined') {
        if (refreshTimerRef.current) {
          window.clearTimeout(refreshTimerRef.current)
        }
        if (!cardRef.current.collapsed) {
          refreshTimerRef.current = window.setTimeout(() => {
            void fetchItems({ background: true })
          }, REFRESH_INTERVAL_MS)
        } else {
          refreshTimerRef.current = null
        }
      }
    },
    [ageMaxDays, dateBasis, fetchUsageFlags, onRequestSnapshotRefresh, stationId, usesSnapshot],
  )

  const handleRefreshPlaceholder = React.useCallback(
    async (id: number) => {
      setPlaceholderRefreshingIds((previous) =>
        previous.includes(id) ? previous : [...previous, id],
      )

      try {
        const existingIndex = entriesRef.current.findIndex(
          (entry) => entry.kind === 'placeholder' && entry.id === id,
        )
        if (existingIndex === -1) {
          return
        }

        const detail = await fetchPrepItemDetailWithRetry(id)
        const normalizedItem = coerceDetailToPrepListItem(detail)

        const currentEntries = entriesRef.current
        const placeholderIndex = currentEntries.findIndex(
          (entry) => entry.kind === 'placeholder' && entry.id === id,
        )

        if (placeholderIndex === -1) {
          return
        }

        const nextEntries: PrepDashboardNormalizedEntry[] = [...currentEntries]
        nextEntries[placeholderIndex] = { kind: 'item', item: normalizedItem }
        entriesRef.current = nextEntries
        setEntries(nextEntries)

        const nextItems = nextEntries.flatMap((entry) =>
          entry.kind === 'item' ? [entry.item] : [],
        )
        itemsRef.current = nextItems
        onItemsChangeRef.current?.(nextItems, totalRef.current)

        void fetchItems({ background: true })
      } catch (error) {
        console.error('Unable to refresh category placeholder', error)
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to refresh prep item.')
            : 'Unable to refresh prep item.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        setPlaceholderRefreshingIds((previous) => previous.filter((value) => value !== id))
      }
    },
    [fetchItems, onItemsChangeRef, setSnackbar],
  )

  React.useEffect(() => {
    if (usesSnapshot) {
      return
    }
    if (card.collapsed) {
      if (refreshTimerRef.current && typeof window !== 'undefined') {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      return
    }
    const run = async () => {
      const delay = initialFetchDoneRef.current ? 0 : Math.max(0, initialDelayRef.current || 0)
      if (delay > 0) {
        await wait(delay)
      }
      initialFetchDoneRef.current = true
      debugLog('load triggered', { cardId: card.id, categoryId: card.categoryId, delayMs: delay })
      await fetchItems()
    }
    void run()
  }, [ageMaxDays, dateBasis, card.categoryId, card.page, card.itemsPerPage, card.collapsed, fetchItems, debugLog, usesSnapshot])

  const toggleMyPrepForItem = React.useCallback(
    async (item: PrepListItem, next: boolean) => {
      markBusy(item.id)
      const currentStoreState = usePrepListStore.getState()
      const previousInMyPrep = Boolean(item.is_in_my_prep || currentStoreState.myIds.has(item.id))
      const previousOrder = [...currentStoreState.myAllOrderedIds]

      if (previousInMyPrep === next) {
        clearBusy(item.id)
        return
      }

      updateItemLocal(item.id, { is_in_my_prep: next })
      updateMyItemInStore(item, next)
      const optimisticOrder = [...usePrepListStore.getState().myAllOrderedIds]
      usePrepDashboardStore.getState().patchMyPrepMembership(
        item.id,
        next,
        item,
        optimisticOrder,
      )

      if (next) {
        incMyCount()
        incMyCategoryCounts(item)
      } else if (previousInMyPrep) {
        decMyCount()
        decMyCategoryCounts(item)
      }

      try {
        const response = next
          ? await addPrepItemToMyPrep(item.id)
          : await removePrepItemFromMyPrep(item.id)
        syncAuthoritativeMyPrepOrder(response, optimisticOrder)
        if (next) {
          setSnackbar({ message: 'Added to My Prep', severity: 'success' })
        } else {
          setSnackbar({ message: 'Removed from My Prep', severity: 'success' })
        }
        void onRequestMyPrepRefresh?.({ itemId: item.id, inMyPrep: next })
      } catch (error) {
        updateItemLocal(item.id, { is_in_my_prep: previousInMyPrep })
        updateMyItemInStore(item, previousInMyPrep)
        syncAuthoritativeMyPrepOrder(null, previousOrder)
        if (next) {
          decMyCount()
          decMyCategoryCounts(item)
        } else if (previousInMyPrep) {
          incMyCount()
          incMyCategoryCounts(item)
        }
        const requestId = extractRequestId(error)
        const message = next ? 'Unable to add to My Prep.' : 'Unable to remove from My Prep.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        clearBusy(item.id)
      }
    },
    [
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      onRequestMyPrepRefresh,
      updateItemLocal,
      updateMyItemInStore,
    ],
  )

  const dismissForMeHandler = React.useCallback(
    async (item: PrepListItem) => {
      markBusy(item.id)
      const {
        previousItems,
        previousEntries,
        previousTotal,
        removed,
        nextTotal,
        index,
        entryIndex,
      } = optimisticRemove(item)
      if (!removed) {
        clearBusy(item.id)
        return
      }

      const storeState = usePrepListStore.getState()
      const wasInMyPrep = Boolean(item.is_in_my_prep || storeState.myIds.has(item.id))
      if (wasInMyPrep) {
        updateMyItemInStore(item, false)
        decMyCount()
        decMyCategoryCounts(item)
        storeState.bumpMyMembershipVersion()
      }

      lastDismissedRef.current = { item, index, entryIndex, wasInMyPrep }

      try {
        await dismissPrepItem(item.id)
        setSnackbar({ message: 'Dismissed for you.', severity: 'success', action: 'undo-dismiss' })
        adjustAfterRemoval(nextTotal)
      } catch (error) {
        rollbackRemoval({ previousItems, previousEntries, previousTotal })
        if (wasInMyPrep) {
          updateMyItemInStore(item, true)
          incMyCount()
          incMyCategoryCounts(item)
          usePrepListStore.getState().bumpMyMembershipVersion()
        }
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to dismiss item.')
            : 'Unable to dismiss item.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      optimisticRemove,
      rollbackRemoval,
      updateMyItemInStore,
    ],
  )

  const markUsedForMeHandler = React.useCallback(
    async (item: PrepListItem) => {
      if (!stationId) {
        throw new Error('No station selected.')
      }

      markBusy(item.id)
      const { previousItems, previousEntries, previousTotal, removed, nextTotal } =
        optimisticRemove(item)
      if (!removed) {
        clearBusy(item.id)
        return
      }

      const mutation = beginPrepUsedOptimistic({
        item,
        userId: me.user.id,
        userName: me.user.name,
      })
      if (!mutation.owner) {
        clearBusy(item.id)
        return
      }
      if (mutation.wasInMyPrep) {
        decMyCategoryCounts(item)
      }

      try {
        const response = await markUsed(stationId, item.id)
        if (!response) {
          throw new Error('Unable to mark as used.')
        }
        if (!commitPrepUsed(mutation, response)) {
          return
        }
        setSnackbar({ message: 'Marked as used.', severity: 'success' })
        adjustAfterRemoval(nextTotal)
        const categoryIds = collectCategoryIds(item)
        if (categoryIds.length > 0 && onRefreshCategories) {
          void onRefreshCategories(categoryIds)
        }
      } catch (error) {
        if (!rollbackPrepUsed(mutation)) {
          return
        }
        rollbackRemoval({ previousItems, previousEntries, previousTotal })
        if (mutation.wasInMyPrep) {
          incMyCategoryCounts(item)
        }
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to mark as used.')
            : 'Unable to mark as used.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
        throw error
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      markUsed,
      me.user.id,
      me.user.name,
      onRefreshCategories,
      optimisticRemove,
      rollbackRemoval,
      stationId,
    ],
  )

  const dismissForAllHandler = React.useCallback(
    async (item: PrepListItem) => {
      markBusy(item.id)
      const { previousItems, previousEntries, previousTotal, removed, nextTotal } =
        optimisticRemove(item)
      if (!removed) {
        clearBusy(item.id)
        return
      }

      const storeState = usePrepListStore.getState()
      const wasInMyPrep = Boolean(item.is_in_my_prep || storeState.myIds.has(item.id))
      if (wasInMyPrep) {
        updateMyItemInStore(item, false)
        decMyCount()
        decMyCategoryCounts(item)
        storeState.bumpMyMembershipVersion()
      }

      try {
        await dismissForAllApi(item.id)
        setSnackbar({ message: 'Dismissed for everyone.', severity: 'success' })
        adjustAfterRemoval(nextTotal)
      } catch (error) {
        rollbackRemoval({ previousItems, previousEntries, previousTotal })
        if (wasInMyPrep) {
          updateMyItemInStore(item, true)
          incMyCount()
          incMyCategoryCounts(item)
          usePrepListStore.getState().bumpMyMembershipVersion()
        }
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to dismiss for everyone.')
            : 'Unable to dismiss for everyone.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      optimisticRemove,
      rollbackRemoval,
      updateMyItemInStore,
    ],
  )

  const actions = React.useMemo<DashboardCardItemActions>(
    () => ({
      getItemById: (id) => itemsRef.current.find((item) => item.id === id) ?? null,
      toggleMyPrep: async (itemId, next) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await toggleMyPrepForItem(item, next)
      },
      dismissForMe: async (itemId) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await dismissForMeHandler(item)
      },
      markUsedForMe: async (itemId) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await markUsedForMeHandler(item)
      },
      dismissForAll: async (itemId) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await dismissForAllHandler(item)
      },
      refreshItems: async (options) => {
        await fetchItems({ background: options?.background ?? true })
      },
    }),
    [dismissForAllHandler, dismissForMeHandler, fetchItems, markUsedForMeHandler, toggleMyPrepForItem],
  )

  React.useEffect(() => {
    onRegisterActions?.(actions)
    return () => {
      onRegisterActions?.(null)
    }
  }, [actions, onRegisterActions])

  React.useEffect(() => {
    if (!activeItemId) {
      return
    }
    const exists = itemsRef.current.some((entry) => entry.id === activeItemId)
    if (!exists) {
      onActiveItemMissing?.()
    }
  }, [activeItemId, onActiveItemMissing, entries])

  const handleUndoDismiss = React.useCallback(async () => {
    const snapshot = lastDismissedRef.current
    if (!snapshot) {
      return
    }

    markBusy(snapshot.item.id)

    try {
      await undoDismissPrepItem(snapshot.item.id)
      const currentItems = itemsRef.current
      const insertIndex = Math.min(Math.max(snapshot.index, 0), currentItems.length)
      const nextItems = [...currentItems]
      nextItems.splice(insertIndex, 0, snapshot.item)
      itemsRef.current = nextItems
      const currentEntries = entriesRef.current
      const entryInsertIndex = Math.min(
        Math.max(snapshot.entryIndex >= 0 ? snapshot.entryIndex : snapshot.index, 0),
        currentEntries.length,
      )
      const nextEntries = [...currentEntries]
      nextEntries.splice(entryInsertIndex, 0, { kind: 'item', item: snapshot.item })
      entriesRef.current = nextEntries
      setEntries(nextEntries)
      const nextTotal = totalRef.current + 1
      totalRef.current = nextTotal
      setTotal(nextTotal)
      onItemsChangeRef.current?.(nextItems, nextTotal)
      if (snapshot.wasInMyPrep) {
        updateMyItemInStore(snapshot.item, true)
        incMyCount()
        incMyCategoryCounts(snapshot.item)
        void addPrepItemToMyPrep(snapshot.item.id).catch((error) => {
          if (process.env.NODE_ENV !== 'production') {
            console.error('[prep] Failed to restore My Prep after undo dismiss', error)
          }
        })
      }
      setSnackbar({ message: 'Dismiss undone.', severity: 'success' })
      lastDismissedRef.current = null
      if (!cardRef.current.collapsed) {
        void fetchItems({ background: true })
      }
    } catch (error) {
      const requestId = extractRequestId(error)
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message ?? 'Unable to undo dismiss.')
          : 'Unable to undo dismiss.'
      setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
    } finally {
      clearBusy(snapshot.item.id)
    }
  }, [
    clearBusy,
    fetchItems,
    incMyCategoryCounts,
    incMyCount,
    markBusy,
    updateMyItemInStore,
  ])

  const handleSnackbarClose = React.useCallback(() => {
    setSnackbar(null)
  }, [])

  const handleSelectItem = React.useCallback(
    (item: PrepListItem) => {
      onSelectItem?.(item)
    },
    [onSelectItem],
  )

  const handleChangePage = React.useCallback(
    (page: number) => {
      onChange({ page })
    },
    [onChange],
  )

  const handleToggleCollapse = React.useCallback(
    (collapsed: boolean) => {
      onChange({ collapsed })
    },
    [onChange],
  )

  const perPage = sanitizePositiveInt(card.itemsPerPage ?? 5, 5, 3, 15)
  const page = sanitizePositiveInt(card.page ?? 1, 1, 1, Number.MAX_SAFE_INTEGER)
  const collapsed = Boolean(card.collapsed)

  const fallbackLabel = React.useMemo(
    () => (card.categoryId ? `Category #${card.categoryId}` : 'Category'),
    [card.categoryId],
  )

  const resolvedCategoryName = React.useMemo(() => {
    const fromStore = typeof titleFromStore === 'string' ? titleFromStore.trim() : ''
    if (fromStore) {
      return fromStore
    }
    const local = categoryName.trim()
    if (local) {
      return local
    }
    return ''
  }, [categoryName, titleFromStore])

  const displayTotal = React.useMemo(() => {
    if (totalKnown) {
      return total
    }
    if (total > 0) {
      return total
    }
    return null
  }, [total, totalKnown])

  const titleReady = Boolean(resolvedCategoryName)

  const hasRealItems = entries.some((entry) => entry.kind === 'item')
  const hasPlaceholders = entries.some((entry) => entry.kind === 'placeholder')
  const showEmptyState = !loading && !error && !hasRealItems && !hasPlaceholders
  const title = resolvedCategoryName || fallbackLabel

  const skeletonCount = Math.min(6, Math.max(4, perPage))

  const resolvedCategorySlug = React.useMemo(() => {
    const provided = typeof card.categorySlug === 'string' ? card.categorySlug.trim() : ''
    if (provided) {
      return provided
    }
    const cached = categoriesById?.[card.categoryId]?.slug
    const cachedTrimmed = typeof cached === 'string' ? cached.trim() : ''
    if (cachedTrimmed) {
      return cachedTrimmed
    }
    const local = categorySlug?.trim() ?? ''
    if (local) {
      return local
    }
    return ''
  }, [card.categoryId, card.categorySlug, categoriesById, categorySlug])

  const normalizedSlug = React.useMemo(() => {
    if (!resolvedCategorySlug) {
      return null
    }
    return resolvedCategorySlug.toLowerCase()
  }, [resolvedCategorySlug])

  const headerTitle = React.useMemo(() => {
    const trimmed = resolvedCategoryName.trim()
    const baseLabel = trimmed || ''
    const shortened = baseLabel ? shortenCategoryLabel(baseLabel) : null
    const label = baseLabel ? shortened?.label || baseLabel : ''
    const suffix = displayTotal === null || !label ? '' : ` (${displayTotal})`

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          minHeight: 36,
          width: '100%',
        }}
        aria-busy={!titleReady || undefined}
      >
        {label ? (
          <Chip
            size="small"
            component="div"
            tabIndex={-1}
            label={`${label}${suffix}`}
            sx={buildCategoryChipSx(normalizedSlug)}
          />
        ) : (
          <Skeleton
            variant="rounded"
            width={140}
            height={28}
            sx={{
              borderRadius: 999,
              transform: 'translateY(1px)',
            }}
            aria-label="Loading category name"
          />
        )}
      </Box>
    )
  }, [normalizedSlug, resolvedCategoryName, titleReady, displayTotal])

  const settingsName = React.useMemo(() => resolvedCategoryName || title, [resolvedCategoryName, title])

  const handleOpenSettings = React.useCallback(() => {
    if (!onOpenSettings) {
      return
    }
    onOpenSettings({
      cardId: card.id,
      type: 'category',
      name: settingsName,
      slug: normalizedSlug,
    })
  }, [card.id, normalizedSlug, onOpenSettings, settingsName])

  const parseUsedAtEpoch = React.useCallback((raw: string | null | undefined): number | null => {
    const value = typeof raw === 'string' ? raw.trim() : ''
    if (!value) {
      return null
    }
    // Treat stored timestamps as UTC; append Z so Date.parse doesn't assume local time.
    const asIsoUtc = `${value.replace(' ', 'T')}Z`
    const parsed = Date.parse(asIsoUtc)
    return Number.isFinite(parsed) ? parsed : null
  }, [])

  const formatUsageEntry = React.useCallback(
    (entry: { used_at?: string | null; user_id?: number | null; used_by_display_name?: string | null }) => {
      const who =
        (entry.used_by_display_name && entry.used_by_display_name.trim()) ||
        (entry.user_id ? 'another presenter' : null)
      const tz = resolveStationTimeZone(stationTimezone)
      const rawUsedAt = typeof entry.used_at === 'string' ? entry.used_at.trim() : ''
      const usedAtEpoch = parseUsedAtEpoch(rawUsedAt)
      if (usedAtEpoch) {
        const timeLabel = formatStationTime(usedAtEpoch, tz, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
        const dayDiff = stationDayDiff(Date.now(), usedAtEpoch, tz)
        let whenLabel: string
        if (dayDiff === 0) {
          whenLabel = `today at ${timeLabel}`
        } else if (dayDiff === 1) {
          whenLabel = `yesterday at ${timeLabel}`
        } else {
          const dateLabel = formatStationTime(usedAtEpoch, tz, {
            weekday: 'long',
            day: 'numeric',
          })
          whenLabel = `${dateLabel} at ${timeLabel}`
        }
        return `${who ?? 'Another presenter'} — ${whenLabel}`
      }
      return rawUsedAt ? `${who ?? 'Another presenter'} — ${rawUsedAt}` : null
    },
    [parseUsedAtEpoch, stationTimezone],
  )

  const formatUsedTooltip = React.useCallback(
    (usage?: { used_at?: string | null; used_by_display_name?: string | null; used_by_user_id?: number | null; used?: boolean; usages?: Array<{ used_at?: string | null; user_id?: number | null; used_by_display_name?: string | null }> }) => {
      if (!usage?.used) {
        return undefined
      }

      const entries = Array.isArray(usage.usages) && usage.usages.length > 0
        ? usage.usages
        : [
            {
              used_at: usage.used_at ?? null,
              user_id: usage.used_by_user_id ?? null,
              used_by_display_name: usage.used_by_display_name ?? null,
            },
          ]

      const lines: string[] = []
      for (const entry of entries.slice(0, 3)) {
        const line = formatUsageEntry(entry)
        if (line) {
          lines.push(line)
        }
      }

      const remaining = entries.length - lines.length
      if (remaining > 0) {
        lines.push(`+${remaining} more`)
      }

      return lines.length ? lines.join(' • ') : undefined
    },
    [formatUsageEntry],
  )

  const handleManualRetry = React.useCallback(() => {
    setManualRetryVisible(false)
    debugLog('manual retry click', { cardId: card.id, categoryId: card.categoryId })
    void fetchItems()
  }, [card.categoryId, card.id, debugLog, fetchItems])

  const handleOpenMenu = React.useCallback((event: React.MouseEvent<HTMLElement>, id: number) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: event.currentTarget }))
  }, [])

  const handleCloseMenu = React.useCallback((id: number) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: null }))
  }, [])

  const handleEditCategories = React.useCallback((item: PrepListItem) => {
    setEditCategoriesItem(item)
  }, [])

  const showRetrySpinner = retryingAttempt !== null || (loading && !totalKnown) || refreshing

  const headerAdornment = React.useMemo(
    () => (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {showRetrySpinner ? <CircularProgress size={18} thickness={5} /> : null}
        {manualRetryVisible ? (
          <Tooltip title="Retry loading category" enterDelay={300}>
            <span>
              <IconButton
                aria-label="Retry loading category"
                size="small"
                onClick={handleManualRetry}
                disabled={loading || refreshing}
              >
                <AutorenewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Stack>
    ),
    [handleManualRetry, loading, manualRetryVisible, refreshing, showRetrySpinner],
  )

  const accentSx = React.useMemo<SxProps<Theme> | undefined>(() => {
    if (!normalizedSlug) {
      return undefined
    }

    const { accent, textOnSolid } = getCategoryColor(normalizedSlug)
    const trimmedAccent = typeof accent === 'string' ? accent.trim() : ''

    if (!trimmedAccent) {
      return undefined
    }

    return {
      '& > header': {
        backgroundColor: trimmedAccent,
        color: textOnSolid,
        '.MuiIconButton-root': {
          color: textOnSolid,
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: alpha(textOnSolid, 0.16),
          },
        },
      },
    }
  }, [normalizedSlug])

  const cardPaperProps = React.useMemo<PaperProps | undefined>(() => {
    if (!accentSx) {
      return undefined
    }

    return { sx: accentSx }
  }, [accentSx])

  let content: React.ReactNode

  if (loading) {
    content = (
      <Stack spacing={1.25} sx={{ pt: 0.5 }}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={28} animation="wave" />
        ))}
      </Stack>
    )
  } else if (error) {
    content = (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Unable to load this category right now. Retry above.
        </Typography>
      </Box>
    )
  } else if (showEmptyState) {
    content = (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No items in this category.
        </Typography>
      </Box>
    )
  } else {
    content = (
      <List
        disablePadding
        sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}
      >
        {entries.map((entry) => {
          if (entry.kind === 'placeholder') {
            const refreshingPlaceholder = placeholderRefreshingIds.includes(entry.id)
            return (
              <ListItem
                key={`placeholder-${entry.id}`}
                disablePadding
                divider
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 0,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    py: 1,
                    px: 0.25,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <PrepDashboardPlaceholderItem
                    id={entry.id}
                    slotText={entry.placeholderText}
                    refreshing={refreshingPlaceholder}
                    onRefresh={() => {
                      void handleRefreshPlaceholder(entry.id)
                    }}
                  />
                </Box>
              </ListItem>
            )
          }

          const item = entry.item
          const isBusy = busyIds.includes(item.id)
          const headline = item.headline?.trim() || `Prep item #${item.id}`
          const tooltip = item.short_preview?.trim() || ''
          const tooltipTitle = (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                component="div"
                sx={(theme) => ({
                  fontWeight: 600,
                  fontSize: theme.typography.pxToRem(15),
                  lineHeight: 1.4,
                  color: 'inherit',
                })}
              >
                {headline}
              </Typography>
              {tooltip ? (
                <Typography
                  component="div"
                  sx={(theme) => ({
                    mt: 0.5,
                    fontSize: theme.typography.pxToRem(14),
                    lineHeight: 1.5,
                    color: 'inherit',
                    whiteSpace: 'pre-line',
                  })}
                >
                  {tooltip}
                </Typography>
              ) : null}
            </Box>
          )
          const selected = activeItemId === item.id
          const isStarred = item.is_in_my_prep || myIdsSet.has(item.id)
          const toggleLabel = isStarred ? 'Remove from My Prep' : 'Add to My Prep'

          const headlineNode = (
            <Typography
              variant="body2"
              sx={[
                { fontWeight: 500 },
                headlineClampSx(headlineLines),
                { minWidth: 0 },
              ]}
              component="span"
            >
              {headline}
            </Typography>
          )

          const imageThumb =
            typeof item.image?.thumb === 'string' ? item.image.thumb.trim() : ''
          const imageAlt = typeof item.image?.alt === 'string' ? item.image.alt : ''
          const showThumbnail = listShowImages && imageThumb.length > 0

          const usage = usageByPrepId.get(item.id)
          const usedTooltip = formatUsedTooltip(usage)
          const usedIcon =
            usage?.used === true ? (
              <Tooltip title={usedTooltip || 'Used today'} enterDelay={250} describeChild>
                <span>
                  <IconButton
                    aria-label="Used today"
                    size="small"
                    edge="end"
                    disableRipple
                    disableFocusRipple
                    tabIndex={-1}
                    sx={{
                      color: (theme) => theme.palette.warning.main,
                      pointerEvents: 'none',
                    }}
                  >
                    <CheckCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null

          const textContent = (
            <Box
              sx={[
                { position: 'relative', minWidth: 0, overflow: 'hidden' },
                showThumbnail
                  ? {
                      pr: 6,
                      minHeight: 48,
                    }
                  : null,
              ]}
            >
              {tooltipTitle ? (
                <Tooltip
                  title={tooltipTitle}
                  placement="top-start"
                  enterDelay={400}
                  describeChild
                  slotProps={itemTooltipSlotProps}
                >
                  <Box component="span" sx={{ display: 'block', minWidth: 0 }}>{headlineNode}</Box>
                </Tooltip>
              ) : (
                headlineNode
              )}
              {showThumbnail ? (
                <Box
                  component="img"
                  src={imageThumb}
                  alt={imageAlt}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
                  }}
                  loading="lazy"
                />
              ) : null}
            </Box>
          )

          const actionsNode = (
            <Box
              sx={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0.15,
                '& .MuiIconButton-root': { p: 0.25 },
              }}
            >
              <Tooltip title={toggleLabel} enterDelay={300} describeChild>
                <span>
                  <IconButton
                    aria-label={toggleLabel}
                    size="small"
                    onClick={() => void toggleMyPrepForItem(item, !isStarred)}
                    disabled={isBusy}
                  >
                    {isStarred ? (
                      <StarIcon fontSize="small" color="warning" />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              {(() => {
                const showClockUsage = clocksByPrepId.get(item.id) ?? []
                const inLiveOrFutureClock = showClockUsage.length > 0
                const tooltipTitle = inLiveOrFutureClock ? (
                  (() => {
                    const { clocks, extraCount } = capShowClockUsage(showClockUsage, 3)
                    return (
                      <Box sx={{ py: 0.25 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          In live/future show clocks
                        </Typography>
                        <Box component="div" sx={{ mt: 0.5 }}>
                          {clocks.map((clock, index) => {
                            const usageItems = Array.isArray(clock.items) ? clock.items : []
                            const { items, extraCount: extraItems } = capShowClockUsageItems(usageItems, 3)

                            return (
                              <Box key={clock.day_id} component="div" sx={{ mt: index === 0 ? 0 : 0.5 }}>
                                <Box component="div" sx={{ typography: 'caption', fontWeight: 600 }}>
                                  {formatShowClockUsageLabel(clock)}
                                </Box>
                                {items.map((item) => (
                                  <Box key={item.day_item_id} component="div" sx={{ typography: 'caption', ml: 1 }}>
                                    {formatShowClockUsageItemLabel(item)}
                                  </Box>
                                ))}
                                {extraItems > 0 ? (
                                  <Box component="div" sx={{ typography: 'caption', ml: 1, mt: 0.25 }}>
                                    +{extraItems} more
                                  </Box>
                                ) : null}
                              </Box>
                            )
                          })}
                          {extraCount > 0 ? (
                            <Box component="div" sx={{ typography: 'caption', mt: 0.25 }}>
                              +{extraCount} more
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                    )
                  })()
                ) : (
                  'Add to Show Clock'
                )

                return (
                  <Tooltip title={tooltipTitle} enterDelay={300} describeChild>
                    <span>
                      <IconButton
                        aria-label="Add to Show Clock"
                        size="small"
                        onClick={() => setAddToShowClockTarget(item)}
                        disabled={isBusy || !stationId}
                        color={inLiveOrFutureClock ? 'success' : 'default'}
                      >
                        <AccessTimeIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              })()}
              <Tooltip title="Dismiss for me" enterDelay={300} describeChild>
                <span>
                  <IconButton
                    aria-label="Dismiss for me"
                    size="small"
                    onClick={() => void dismissForMeHandler(item)}
                    disabled={isBusy}
                  >
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {usedIcon}
              {isSuperadmin ? (
                <>
                  <Tooltip title="More actions" enterDelay={300} describeChild>
                    <span>
                      <IconButton
                        aria-label="More actions"
                        size="small"
                        onClick={(event) => handleOpenMenu(event, item.id)}
                        disabled={isBusy}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Menu
                    anchorEl={menuAnchor[item.id] ?? null}
                    open={Boolean(menuAnchor[item.id])}
                    onClose={() => handleCloseMenu(item.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleCloseMenu(item.id)
                        void dismissForAllHandler(item)
                      }}
                    >
                      Archive for everyone
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleCloseMenu(item.id)
                        handleEditCategories(item)
                      }}
                    >
                      Edit category
                    </MenuItem>
                  </Menu>
                </>
              ) : null}
            </Box>
          )

          return (
            <ListItem
              key={item.id}
              disablePadding
              divider
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 0,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  <ListItemButton
                    onClick={() => handleSelectItem(item)}
                    selected={selected}
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      py: 0.85,
                      px: 0.25,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                      {textContent}
                    </Box>
                  </ListItemButton>
                </Box>
                {actionsNode}
              </Box>
            </ListItem>
          )
        })}
      </List>
    )
  }

  const snackbarAction =
    snackbar?.action === 'undo-dismiss' ? (
      <Button color="inherit" size="small" onClick={() => void handleUndoDismiss()}>
        Undo
      </Button>
    ) : undefined

  return (
    <>
      <DashboardCard
        title={title}
        titleContent={headerTitle}
        page={page}
        perPage={perPage}
        total={total}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        onChangePage={handleChangePage}
        loading={loading}
        refreshing={refreshing}
        headerAdornment={headerAdornment}
        paperProps={cardPaperProps}
        onOpenSettings={handleOpenSettings}
      >
        {content}
      </DashboardCard>
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={snackbar?.action ? 6000 : 4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} action={snackbarAction} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>
      <AddPrepToShowClockModal
        open={Boolean(addToShowClockTarget)}
        stationId={stationId}
        prepItemId={addToShowClockTarget?.id ?? 0}
        prepHeadline={addToShowClockTarget?.headline ?? 'Prep item'}
        onClose={() => setAddToShowClockTarget(null)}
        onSuccessToast={(message) => setSnackbar({ message, severity: 'success' })}
      />
      <EditPrepCategoriesModal
        open={Boolean(editCategoriesItem)}
        item={editCategoriesItem as PrepListItem}
        onClose={() => setEditCategoriesItem(null)}
        onSaved={(updated) => {
          const previousIds =
            Array.isArray(editCategoriesItem?.categories) && editCategoriesItem?.categories.length
              ? editCategoriesItem?.categories
                  .map((c) => (c && typeof c.id === 'number' ? Math.floor(c.id) : null))
                  .filter((id): id is number => id !== null)
              : []
          setEditCategoriesItem(null)
          // optimistic patch items in this card
          if (entriesRef.current.length > 0) {
            const nextEntries = entriesRef.current.map((entry) => {
              if (entry.kind !== 'item' || entry.item.id !== updated.id) {
                return entry
              }
              return { kind: 'item', item: { ...entry.item, ...updated } }
            })
            entriesRef.current = nextEntries
            const nextItems = nextEntries.flatMap((entry) => (entry.kind === 'item' ? [entry.item] : []))
            itemsRef.current = nextItems
            setEntries(nextEntries)
            onItemsChangeRef.current?.(nextItems, totalRef.current)
          }
          // background refresh to ensure counts reflect any category moves
          const updatedIds =
            Array.isArray(updated.categories) && updated.categories.length > 0
              ? updated.categories
                  .map((entry) => (entry && typeof entry.id === 'number' ? Math.floor(entry.id) : null))
                  .filter((id): id is number => id !== null)
              : []
          const allIds = Array.from(new Set([...previousIds, ...updatedIds]))
          if (allIds.length && onRefreshCategories) {
            onRefreshCategories(allIds)
          }
          void fetchItems({ background: true })
        }}
      />
    </>
  )
}

export default CategoryCard
